import { bulkActionFactory } from '@core/async-job-data';
import { AsyncJobType } from '@core/async-job-types';
import type { BulkCandidateResponse, BulkFilter } from '@core/bulk-actions-types';
import { createEntityCollectionStore } from '@core/ngrx-legacy';

import type { BulkRequestEvaluationsParams, Ids } from './bulk-actions-http-client';

const idSelector = ({ uuid }: BulkCandidateResponse) => uuid;
const { actions, namespace, reducer, selectors } =
  createEntityCollectionStore<BulkCandidateResponse>()('candidate-bulk-actions', {
    idSelector,

    // eslint-disable-next-line max-lines-per-function
    customActions: adapter => {
      const getActions = bulkActionFactory(adapter);
      return {
        ...getActions('addTask', AsyncJobType.BulkCandidatesAddTask)<
          Ids & { task: any; filter?: BulkFilter }
        >(),
        ...getActions('delete', AsyncJobType.BulkCandidatesDeleteCandidates)<
          Ids & { filter?: BulkFilter }
        >(),
        ...getActions('follow', AsyncJobType.BulkCandidatesFollow)<Ids & { filter?: BulkFilter }>(),
        ...getActions('unfollow', AsyncJobType.BulkCandidatesUnfollow)<
          Ids & { filter?: BulkFilter }
        >(),
        ...getActions('requalify', AsyncJobType.BulkCandidatesRequalify)<
          Ids & { offerId: number; filter?: BulkFilter }
        >(),
        ...getActions('disqualify', AsyncJobType.BulkCandidatesDisqualify)<
          Ids & {
            offerId: number;
            disqualifyReasonId: number;
            runActions?: boolean | null;
            filter?: BulkFilter;
          }
        >(),
        ...getActions('assign', AsyncJobType.BulkCandidatesAssign)<
          Ids & {
            offersIds?: number[];
            talentPoolIds?: number[];
            offers?: Array<{ id: number; location_ids: number[] }>;
            runActions?: boolean | null;
            filter?: BulkFilter;
          }
        >(),
        ...getActions('unassign', AsyncJobType.BulkCandidatesUnassign)<
          Ids & { offersIds: number[]; talentPoolIds?: number[]; filter?: BulkFilter }
        >(),
        ...getActions('changeStage', AsyncJobType.BulkCandidatesChangeStage)<
          Ids & {
            offerId: number;
            stageId: number;
            runActions?: boolean | null;
            hiredAt?: string;
            jobStartsAt?: string;
            filter?: BulkFilter;
          }
        >(),
        ...getActions('addTags', AsyncJobType.BulkCandidatesAddTags)<
          Ids & { items: string[]; filter?: BulkFilter }
        >(),
        ...getActions('addSources', AsyncJobType.BulkCandidatesAddSources)<
          Ids & { items: string[]; filter?: BulkFilter }
        >(),
        ...getActions('addLocations', AsyncJobType.BulkCandidatesAddLocations)<
          Ids & { offerId: number; locationIds: number[]; filter?: BulkFilter }
        >(),
        ...getActions(
          'extendGdprExpirationDate',
          AsyncJobType.BulkCandidatesSetGdprCustomExpiresAt,
        )<Ids & { gdprCustomExpiresAt: string; filter?: BulkFilter }>(),
        ...getActions('retrieve', AsyncJobType.BulkCandidatesRetrieveCandidates)<
          Ids & { filter?: BulkFilter }
        >(),
        ...getActions('shareWithTeam', AsyncJobType.BulkSharePlacements)<{
          adminIds: number[];
          placementIds: number[];
          message: string;
        }>(),
        ...getActions('screen', AsyncJobType.BulkScreenCandidates)<
          Ids & { offerId?: number | null }
        >(),
        ...getActions(
          'requestEvaluations',
          AsyncJobType.BulkRequestEvaluations,
        )<BulkRequestEvaluationsParams>(),
      };
    },
  });

const CandidateBulkActions = <
  Pick<
    typeof actions,
    | 'addLocations'
    | 'addLocationsSuccess'
    | 'addLocationsFailed'
    | 'addTask'
    | 'addTaskSuccess'
    | 'addTaskFailed'
    | 'delete'
    | 'deleteSuccess'
    | 'deleteFailed'
    | 'follow'
    | 'followSuccess'
    | 'followFailed'
    | 'unfollow'
    | 'unfollowSuccess'
    | 'unfollowFailed'
    | 'requalify'
    | 'requalifySuccess'
    | 'requalifyFailed'
    | 'disqualify'
    | 'disqualifySuccess'
    | 'disqualifyFailed'
    | 'assign'
    | 'assignSuccess'
    | 'assignFailed'
    | 'unassign'
    | 'unassignSuccess'
    | 'unassignFailed'
    | 'changeStage'
    | 'changeStageSuccess'
    | 'changeStageFailed'
    | 'addTags'
    | 'addTagsSuccess'
    | 'addTagsFailed'
    | 'addSources'
    | 'addSourcesSuccess'
    | 'addSourcesFailed'
    | 'extendGdprExpirationDate'
    | 'extendGdprExpirationDateSuccess'
    | 'extendGdprExpirationDateFailed'
    | 'retrieve'
    | 'retrieveSuccess'
    | 'retrieveFailed'
    | 'shareWithTeam'
    | 'shareWithTeamSuccess'
    | 'shareWithTeamFailed'
    | 'screen'
    | 'screenSuccess'
    | 'screenFailed'
    | 'requestEvaluations'
    | 'requestEvaluationsSuccess'
    | 'requestEvaluationsFailed'
  >
>actions;

export {
  CandidateBulkActions,
  selectors as CandidateBulkSelectors,
  reducer as CandidateBulkReducer,
  namespace as CandidateBulkNamespace,
};
